import type {PaymentProvider} from './provider';

export type WalletRecipientRef = {
  id: number;
  standalone?: boolean;
  domain?: {id: number};
  name?: string;
  document?: string;
};

export type WalletDomainRef = {
  id: number;
  name?: string;
  document?: string;
};

export type Wallet = {
  id: number;
  recipient?: WalletRecipientRef;
  domain?: WalletDomainRef;
  integration?: {
    id: number;
    provider: PaymentProvider;
    capabilities?: object;
    description?: string;
  } | null;
  pool?: {
    id: number;
    label: string;
    integration: {id: number; provider: PaymentProvider};
  } | null;
  isPrimary?: boolean;
  isPooled?: boolean;
  routingEnabled?: boolean;
  label?: string;
  agency?: string;
  account?: string;
  ispb?: string;
  holderName?: string;
  availableBalance: number;
  pendingBalance: number;
  withheldBalance: number;
  createdAt: string | Date;
  updatedAt: string | Date;
};

/** Wallet reference embedded on transactions, invoices, withdrawals, etc. */
export type WalletRef = Pick<
  Wallet,
  'id' | 'label' | 'isPrimary' | 'integration' | 'pool'
>;

export function walletProvider(
  w: Pick<Wallet, 'integration' | 'pool'>,
): PaymentProvider | undefined {
  return w.integration?.provider ?? w.pool?.integration?.provider;
}

export function resolveWalletLabel(
  wallet: Wallet | WalletRef | null | undefined,
  options?: {
    providerLabel?: (provider: PaymentProvider) => string;
    fallbackLabel?: (id: number) => string;
  },
): string {
  if (!wallet) {
    return '—';
  }

  const customLabel = wallet.label?.trim();
  if (customLabel) {
    return customLabel;
  }

  if (wallet.pool?.label?.trim()) {
    return wallet.pool.label.trim();
  }

  const provider = walletProvider(wallet);
  if (provider && options?.providerLabel) {
    return options.providerLabel(provider);
  }

  if (wallet.id && options?.fallbackLabel) {
    return options.fallbackLabel(wallet.id);
  }

  return wallet.id ? `#${wallet.id}` : '—';
}

export type WalletBindingScope = 'standalone' | 'pooled';

export function walletBindingScope(wallet: Wallet): WalletBindingScope | null {
  if (wallet.pool) {
    return 'pooled';
  }

  if (wallet.integration) {
    return 'standalone';
  }

  return null;
}

export type CreateWalletPayload = {
  recipientId: number;
  integrationId?: number;
  poolId?: number;
  isPrimary?: boolean;
  label?: string;
  agency?: string;
  account?: string;
  ispb?: string;
  holderName?: string;
};

export type AssociateWalletPayload = {
  integrationId?: number;
  poolId?: number;
};
